import { useCallback, useEffect, useState } from "react";
import { FiArrowLeft, FiCheckCircle, FiDollarSign } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const today = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(
    new Date(),
  );
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

export default function EmployeeAdvanceDetailsPage() {
  const { advanceId } = useParams();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [advance, setAdvance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    amount: "",
    paidOn: today(),
    paymentMode: "CASH",
    paymentReference: "",
    note: "",
  });

  const load = useCallback(async () => {
    if (!accessToken || !advanceId) return;
    try {
      setLoading(true);
      setError("");
      setAdvance(
        await apiRequest(`/employee-advances/${advanceId}`, { accessToken }),
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load this advance.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, advanceId]);

  useEffect(() => {
    load();
  }, [load]);

  const repay = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      await apiRequest(`/employee-advances/${advanceId}/repayments`, {
        method: "POST",
        accessToken,
        body: {
          amount: Number(form.amount),
          paidOn: form.paidOn,
          paymentMode: form.paymentMode,
          paymentReference: form.paymentReference.trim() || undefined,
          note: form.note.trim(),
        },
      });
      setForm({
        amount: "",
        paidOn: today(),
        paymentMode: "CASH",
        paymentReference: "",
        note: "",
      });
      load();
    } catch (requestError) {
      setError(requestError.message || "Unable to record this repayment.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="py-20 text-center text-sm text-gray-500">
        Loading advance…
      </div>
    );

  return (
    <div className="mx-auto space-y-6 pb-10">
      <button
        type="button"
        onClick={() => navigate("/admindashboard/employee-advances")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-950"
      >
        <FiArrowLeft /> Back to advances
      </button>
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {!advance ? null : (
        <>
          <header className="flex flex-col gap-4 rounded-2xl bg-white p-6 text-black sm:flex-row sm:items-end sm:justify-between sm:p-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[.16em] text-primary-800">
                Employee advance
              </p>
              <h1 className="mt-2 text-3xl font-bold">
                {advance.employee?.fullName}
              </h1>
              <p className="mt-1 text-sm text-black">
                {advance.employee?.employeeCode} · Issued{" "}
                {formatDate(advance.issuedOn)}
              </p>
            </div>
            <div className="rounded-xl bg-white/10 px-5 py-4 text-left sm:text-right">
              <p className="text-xs font-semibold uppercase tracking-wide text-black">
                Outstanding
              </p>
              <p className="mt-1 text-3xl font-bold">
                {money(advance.outstandingAmount)}
              </p>
            </div>
          </header>
          <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-lg font-bold text-gray-950">
                Advance record
              </h2>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <Detail
                  label="Amount issued"
                  value={money(advance.issuedAmount)}
                />
                <Detail
                  label="Amount recovered"
                  value={money(advance.recoveredAmount)}
                />
                <Detail
                  label="Payment mode"
                  value={(advance.paymentMode || "—").replaceAll("_", " ")}
                />
                <Detail
                  label="Reference"
                  value={advance.paymentReference || "—"}
                />
                <div className="sm:col-span-2">
                  <Detail label="Reason" value={advance.reason} />
                </div>
              </dl>
              <div className="mt-7 border-t border-gray-100 pt-6">
                <h2 className="text-lg font-bold text-gray-950">
                  Repayment history
                </h2>
                {advance.repayments?.length ? (
                  <div className="mt-3 divide-y divide-gray-100">
                    {advance.repayments.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between gap-5 py-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {formatDate(item.paidOn)} ·{" "}
                            {item.source.replaceAll("_", " ")}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {item.note || "No note"}
                            {item.paymentReference
                              ? ` · Ref: ${item.paymentReference}`
                              : ""}
                          </p>
                        </div>
                        <b className="whitespace-nowrap text-sm text-emerald-700">
                          -{money(item.amount)}
                        </b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-500">
                    No repayments have been recorded yet.
                  </p>
                )}
              </div>
            </div>
            {advance.status === "OPEN" ? (
              <form
                onSubmit={repay}
                className="h-fit rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-6"
              >
                <div className="flex items-center gap-2">
                  <FiDollarSign className="text-primary-700" />
                  <h2 className="text-lg font-bold text-gray-950">
                    Record repayment
                  </h2>
                </div>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Record a cash, bank, or cheque repayment. Payroll recoveries
                  will appear here automatically once enabled.
                </p>
                <div className="mt-5 space-y-4">
                  <Field label="Amount">
                    <input
                      required
                      min="0.01"
                      max={Number(advance.outstandingAmount)}
                      step="0.01"
                      type="number"
                      value={form.amount}
                      onChange={(event) =>
                        setForm({ ...form, amount: event.target.value })
                      }
                      placeholder={`Maximum ${money(advance.outstandingAmount)}`}
                    />
                  </Field>
                  <Field label="Paid on">
                    <input
                      required
                      type="date"
                      value={form.paidOn}
                      onChange={(event) =>
                        setForm({ ...form, paidOn: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Payment mode">
                    <select
                      value={form.paymentMode}
                      onChange={(event) =>
                        setForm({ ...form, paymentMode: event.target.value })
                      }
                    >
                      <option value="CASH">Cash</option>
                      <option value="BANK_TRANSFER">Bank transfer</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </Field>
                  <Field label="Reference">
                    <input
                      value={form.paymentReference}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          paymentReference: event.target.value,
                        })
                      }
                      placeholder="Optional reference"
                    />
                  </Field>
                  <Field label="Note">
                    <textarea
                      required
                      value={form.note}
                      onChange={(event) =>
                        setForm({ ...form, note: event.target.value })
                      }
                      placeholder="Why or how was this collected?"
                    />
                  </Field>
                </div>
                <button
                  disabled={saving}
                  className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? "Saving repayment…" : "Record repayment"}
                </button>
              </form>
            ) : (
              <aside className="h-fit rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-emerald-900">
                <FiCheckCircle size={22} />
                <h2 className="mt-3 font-bold">Advance fully recovered</h2>
                <p className="mt-1 text-sm leading-5">
                  This advance has no remaining balance.
                </p>
              </aside>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold text-gray-900">{value}</dd>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-gray-800">
      <span className="mb-1.5 block">{label}</span>
      <div className="[&_input]:min-h-11 [&_input]:w-full [&_input]:rounded-xl [&_input]:border [&_input]:border-gray-200 [&_input]:px-3.5 [&_input]:text-sm [&_input]:font-normal [&_input]:outline-none [&_input]:focus:border-primary-500 [&_input]:focus:ring-4 [&_input]:focus:ring-primary-100 [&_select]:min-h-11 [&_select]:w-full [&_select]:rounded-xl [&_select]:border [&_select]:border-gray-200 [&_select]:bg-white [&_select]:px-3.5 [&_select]:text-sm [&_select]:font-normal [&_select]:outline-none [&_select]:focus:border-primary-500 [&_select]:focus:ring-4 [&_select]:focus:ring-primary-100 [&_textarea]:min-h-24 [&_textarea]:w-full [&_textarea]:resize-y [&_textarea]:rounded-xl [&_textarea]:border [&_textarea]:border-gray-200 [&_textarea]:px-3.5 [&_textarea]:py-3 [&_textarea]:text-sm [&_textarea]:font-normal [&_textarea]:outline-none [&_textarea]:focus:border-primary-500 [&_textarea]:focus:ring-4 [&_textarea]:focus:ring-primary-100">
        {children}
      </div>
    </label>
  );
}
